import {css} from '@emotion/css';
import {Typography} from '@mui/material';
import Highlighter from 'react-highlight-words';
import RcTree, {TreeNodeProps} from '@ennabl/rc-tree';
import {useMemo, ReactNode, ForwardedRef} from 'react';
import {Merge} from 'type-fest';
import {TreeProps as RcTreeProps} from '@ennabl/rc-tree/es/Tree';
import {FieldDataNode, EventDataNode, BasicDataNode} from '@ennabl/rc-tree/es/interface';

import {addDisabledFlagsToNodes, removeNonLeaveKeys, GenericTreeNode} from 'utils/treeUtils';
import {DecisionsTypographyVariants} from 'core/themes/themeDecisions/typography';

import LineClamp from 'components/LineClamp';
import {
  SCheckboxChecked,
  SCheckboxIndeterminate,
  SCheckboxUnchecked,
  STitleWrapper,
  STree,
  STreeWrapper,
} from 'components/Tree/TreeStyles';
import {TreeSwitcherIcon} from 'components/Tree/TreeSwitcher';
import {TreeBasicNode, TreeKey} from 'components/Tree/TreeTypes';

export const sMotion = css`
  transition: all 0.3s;
  overflow-y: hidden;
`;

const defaultExpandAnimation = {
  motionName: sMotion,
  motionAppear: false,
  onAppearStart: () => ({height: 0}),
  onAppearActive: (node: any) => ({height: node.scrollHeight}),
  onLeaveStart: (node: any) => ({height: node.offsetHeight}),
  onLeaveActive: () => ({height: 0}),
};

const checkboxes = (
  <>
    <SCheckboxUnchecked />
    <SCheckboxIndeterminate />
    <SCheckboxChecked />
  </>
);

const treeIconRenderer = (props: TreeNodeProps) => <TreeSwitcherIcon {...props} />;

export type TreeProps<
  K extends TreeKey,
  T extends TreeBasicNode<K> = TreeBasicNode<K>,
  TD extends GenericTreeNode<T & BasicDataNode> = GenericTreeNode<T & BasicDataNode>
> = Merge<
  Omit<
    RcTreeProps<FieldDataNode<T>>,
    | 'treeData'
    | 'onCheck'
    | 'defaultExpandedKeys'
    | 'expandedKeys'
    | 'defaultCheckedKeys'
    | 'checkedKeys'
    | 'defaultSelectedKeys'
    | 'selectedKeys'
    | 'onExpand'
    | 'onCheck'
    | 'onSelect'
    | 'onLoad'
    | 'loadData'
    | 'loadedKeys'
  >,
  {
    ref?: ForwardedRef<RcTree<T>>;
    treeData: TD[];
    defaultExpandedKeys?: K[];
    disabledKeys?: K[] | Set<K>;
    expandedKeys?: K[];
    defaultCheckedKeys?: K[];
    checkedKeys?: K[] | {checked: K[]; halfChecked: K[]};
    defaultSelectedKeys?: K[];
    selectedKeys?: K[];
    onExpand?: (
      expandedKeys: K[],
      info: {
        node: EventDataNode<TD>;
        expanded: boolean;
        nativeEvent: MouseEvent;
      }
    ) => void;
    onCheck?: (
      checked: K[],
      info: {
        event: 'check';
        node: EventDataNode<TD>;
        checked: boolean;
        nativeEvent: MouseEvent;
        checkedNodes: TD[];
        checkedNodesPositions?: {
          node: TD;
          pos: string;
        }[];
        halfCheckedKeys?: K[];
      }
    ) => void;
    onSelect?: (
      selectedKeys: K[],
      info: {
        event: 'select';
        selected: boolean;
        node: EventDataNode<TD>;
        selectedNodes: TD;
        nativeEvent: MouseEvent;
      }
    ) => void;
    onLoad?: (
      loadedKeys: K[],
      info: {
        event: 'load';
        node: EventDataNode<TD>;
      }
    ) => void;
    loadData?: (treeNode: EventDataNode<TD>) => Promise<any>;
    loadedKeys?: K[];
    prefixCls?: string;
    rightColRenderer?: (node: T) => ReactNode;
    searchQuery?: string;
    nodeTitleVariant?: DecisionsTypographyVariants;
  }
>;

const COMPENSATE_FOR_BORDER = 2;

/**
 * Please note: `checked` always consists only of leave keys
 */
export const Tree = <K extends string | number, T extends TreeBasicNode<K>>({
  treeData,
  rightColRenderer,
  searchQuery,
  children,
  className,
  prefixCls = 'rc-tree',
  selectable = false,
  switcherIcon = TreeSwitcherIcon,
  motion = defaultExpandAnimation,
  onCheck,
  checkable = Boolean(onCheck),
  disabledKeys,
  nodeTitleVariant,
  ref,
  height,
  ...props
}: TreeProps<K, T>) => {
  const transformedTreeData = useMemo<typeof treeData>(() => {
    const size = Array.isArray(disabledKeys) ? disabledKeys.length : disabledKeys?.size;

    if (!disabledKeys || !size) {
      return treeData;
    }

    return addDisabledFlagsToNodes(treeData, disabledKeys);
  }, [disabledKeys, treeData]);

  return (
    <STreeWrapper className={className}>
      {children}
      <STree
        ref={ref}
        treeData={transformedTreeData}
        prefixCls={prefixCls}
        selectable={selectable}
        switcherIcon={switcherIcon ?? treeIconRenderer}
        motion={motion}
        itemHeight={52}
        checkable={checkable ? checkboxes : checkable}
        titleRender={(node: any) => (
          <DefaultTitleRenderer
            node={node}
            rightColRenderer={rightColRenderer as any}
            searchQuery={searchQuery}
            nodeTitleVariant={nodeTitleVariant}
          />
        )}
        onCheck={(checked: any, info: any) => {
          const checkedArr = 'checked' in checked ? checked.checked : checked;
          const checkedClean = removeNonLeaveKeys(treeData, checkedArr);
          onCheck?.(Array.from(checkedClean) as K[], info);
        }}
        height={height ? height - COMPENSATE_FOR_BORDER : undefined}
        {...props}
      />
    </STreeWrapper>
  );
};

function DefaultTitleRenderer<K extends TreeKey, T extends TreeBasicNode<K>>({
  node,
  rightColRenderer,
  searchQuery,
  nodeTitleVariant,
}: {
  node: T;
} & Pick<TreeProps<K, T>, 'rightColRenderer' | 'searchQuery' | 'nodeTitleVariant'>) {
  return (
    <STitleWrapper>
      <LineClamp>
        <Typography variant={nodeTitleVariant ?? 'subtitle2'}>
          <Highlighter autoEscape textToHighlight={String(node.title)} searchWords={[searchQuery || '']}>
            {node.title}
          </Highlighter>
        </Typography>
      </LineClamp>
      {rightColRenderer && rightColRenderer(node as any)}
    </STitleWrapper>
  );
}
